/**
 * MODULE: _shared/frontend-shell/src/components/auth/AuthBrandMark.tsx
 * PURPOSE: Shared Brazn app icon + wordmark for auth/onboarding screens (parity with TopBar [2.2.2023]).
 * RECENT: 2026-08-03 [2.2.2023] — Wordmark **Brazn ai** (capital B).
 */

/** Canonical square app icon (same asset as TopBar and favicons). */
export const BRAZN_APP_ICON_SRC = '/brazn-logo-header-icon.png';

export type AuthBrandMarkSize = 'sm' | 'md' | 'lg';

const ICON_SIZE: Record<AuthBrandMarkSize, string> = {
  sm: 'h-10 w-10',
  md: 'h-12 w-12',
  lg: 'h-16 w-16',
};

const WORDMARK_SIZE: Record<AuthBrandMarkSize, string> = {
  sm: 'text-lg',
  md: 'text-xl',
  lg: 'text-2xl',
};

export interface AuthBrandMarkProps {
  size?: AuthBrandMarkSize;
  /** When false, only the square icon is shown. */
  showWordmark?: boolean;
  className?: string;
  layout?: 'stacked' | 'inline';
}

export function AuthBrandMark({
  size = 'lg',
  showWordmark = true,
  className = '',
  layout = 'stacked',
}: AuthBrandMarkProps) {
  const icon = (
    <img
      src={BRAZN_APP_ICON_SRC}
      alt=""
      className={`${ICON_SIZE[size]} shrink-0 rounded-md object-contain`}
      width={size === 'lg' ? 64 : size === 'md' ? 48 : 40}
      height={size === 'lg' ? 64 : size === 'md' ? 48 : 40}
      aria-hidden
    />
  );

  if (!showWordmark) {
    return <div className={`flex justify-center ${className}`}>{icon}</div>;
  }

  if (layout === 'inline') {
    return (
      <div className={`flex items-center justify-center gap-2.5 ${className}`}>
        {icon}
        <span
          className={`whitespace-nowrap font-semibold tracking-tight text-gray-900 dark:text-white ${WORDMARK_SIZE[size]}`}
        >
          Brazn ai
        </span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`}>
      {icon}
      <span
        className={`whitespace-nowrap font-semibold tracking-tight text-gray-900 dark:text-white ${WORDMARK_SIZE[size]}`}
      >
        Brazn ai
      </span>
    </div>
  );
}
